/**
 * Quality-gate vocabulary for the dashboard: status chips, bucket and
 * condition labels. Meaning is always carried by the word; the tone only
 * reinforces it (AAA inks from the Chip tones).
 */
import type {
  GateBucket,
  GateConditionId,
  GateStatus,
  NotRecheckedReason,
} from "@iamu/shared";
import {
  AlertTriangle,
  Check,
  CircleHelp,
  FilePen,
  type LucideIcon,
  X,
} from "lucide-react";
import { Chip, type ChipTone } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";

const STATUS: Record<
  GateStatus,
  { label: string; tone: ChipTone; icon: LucideIcon }
> = {
  passed: { label: "Passed", tone: "helps", icon: Check },
  warned: {
    label: "Passed with warnings",
    tone: "important",
    icon: AlertTriangle,
  },
  failed: { label: "Failed", tone: "critical", icon: X },
  inconclusive: { label: "Inconclusive", tone: "primary", icon: CircleHelp },
  error: { label: "Error", tone: "critical", icon: X },
};

export function gateStatusLabel(status: GateStatus): string {
  return STATUS[status].label;
}

/** Gate status as a chip ("Failed", "Inconclusive"…). */
export function GateStatusChip({
  status,
  className,
}: {
  status: GateStatus | null | undefined;
  className?: string;
}) {
  if (!status) {
    return (
      <Chip tone="outline" className={className}>
        Not evaluated
      </Chip>
    );
  }
  const { label, tone, icon: Icon } = STATUS[status];
  return (
    <Chip tone={tone} className={className}>
      <Icon aria-hidden="true" />
      {label}
    </Chip>
  );
}

/**
 * The build's runs were taken against uncommitted changes, so no commit
 * names what was tested. Each distinct working tree is its own build.
 */
export function UncommittedChip({ hash }: { hash: string | null }) {
  const content = hash
    ? `Working tree ${hash.slice(0, 12)}: changes not yet committed`
    : "Changes not yet committed";

  return (
    <SimpleTooltip content={content}>
      <Chip tone="outline" className="cursor-help">
        <FilePen aria-hidden="true" />
        Uncommitted changes
      </Chip>
    </SimpleTooltip>
  );
}

export const BUCKET_LABELS: Record<GateBucket, string> = {
  new: "New",
  fixed: "Fixed",
  fix_didnt_hold: "Fix didn't hold",
  regressed: "Regressed",
  still_open: "Still open",
  not_rechecked: "Not re-checked",
  accepted: "Accepted",
  strength_lost: "Strengths lost",
};

export function bucketTone(bucket: GateBucket): ChipTone {
  switch (bucket) {
    case "fixed":
      return "helps";
    case "regressed":
    case "fix_didnt_hold":
      return "critical";
    case "new":
    case "still_open":
    case "strength_lost":
      return "important";
    default:
      return "outline";
  }
}

export const BUCKET_EXPLAINERS: Record<GateBucket, string> = {
  new: "First observed in this build.",
  fixed: "Open at the baseline; a re-run in this build reported it resolved.",
  fix_didnt_hold: "Marked fixed, and a re-run in this build saw it again.",
  regressed: "Verified fixed earlier, and seen again in this build.",
  still_open: "Open at the baseline and seen again in this build.",
  not_rechecked:
    "Open at the baseline, but nothing in this build confirmed it fixed. Never counted as fixed.",
  accepted: "Dismissed or accepted as a risk; does not count against the gate.",
  strength_lost: "Something that helped at the baseline was not seen again.",
};

export const REASON_LABELS: Record<NotRecheckedReason, string> = {
  scenario_not_run: "Scenario not run",
  not_observed: "Walker didn't reach it",
  uncertain: "Matcher unsure",
  not_mentioned: "Dropped from the last run's diff",
};

export const CONDITION_LABELS: Record<GateConditionId, string> = {
  G1: "No new critical problems",
  G2: "No new major problems",
  G3: "No critical or major regressions",
  G4: "No human test fell to No",
  G5: "Applied fixes held",
  G6: "Scenario coverage",
  G7: "Critical and major baseline problems re-checked",
  G8: "No strengths lost",
  G9: "New minor problems within limit",
};

export const BASELINE_MODE_LABELS: Record<string, string> = {
  previous_run: "Previous run per scenario",
  previous_build: "Previous build",
  reference_branch: "Latest build on the reference branch",
  pinned_build: "Pinned build",
  date: "State on a date",
  days: "State N days earlier",
};

/** "3f9c2a1" — short SHA for display. */
export function shortSha(sha: string | null | undefined): string | null {
  return sha ? sha.slice(0, 7) : null;
}
